// ============================================================================
// COMPLIANCE MATRIX TABLE — story #176
// Presentational, cross-document compliance list. Split out from
// ComplianceMatrixView.tsx (which wires it to the data hooks) so this piece
// has no dependency on axios/react-query — keeps it cheaply unit-testable.
// ============================================================================

import React, { useMemo, useState } from 'react';
import { AlertTriangle, CornerDownRight } from 'lucide-react';
import { BidRequirement, BidRequirementStatus } from '../../services/bidService';

const STATUS_META: Record<BidRequirementStatus, { label: string; className: string }> = {
  Covered: { label: 'Covered', className: 'text-green-700 bg-green-50 border-green-200' },
  Partial: { label: 'Partial', className: 'text-amber-700 bg-amber-50 border-amber-200' },
  Gap: { label: 'Gap', className: 'text-red-700 bg-red-50 border-red-200' },
  'N/A': { label: 'N/A', className: 'text-slate-500 bg-slate-50 border-slate-200' }
};

const CHANGE_STATUS_META: Record<NonNullable<BidRequirement['changeStatus']>, string> = {
  new: 'text-green-700 bg-green-50 border-green-200',
  modified: 'text-amber-700 bg-amber-50 border-amber-200',
  removed: 'text-red-700 bg-red-50 border-red-200',
  unchanged: 'text-slate-500 bg-slate-50 border-slate-200'
};

function formatSourceRef(sourceRef: { page?: number; section?: string; sheetName?: string; rowIndex?: number }): string | null {
  if (sourceRef.page !== undefined) return `page ${sourceRef.page}`;
  if (sourceRef.section) return `§ ${sourceRef.section}`;
  if (sourceRef.sheetName !== undefined && sourceRef.rowIndex !== undefined) return `${sourceRef.sheetName} row ${sourceRef.rowIndex + 1}`;
  return null;
}

type SortKey = 'clauseRef' | 'status' | 'weight';

interface MatrixRowProps {
  req: BidRequirement;
  documentName?: string;
  isChild: boolean;
  childCount: number;
}

function MatrixRow({ req, documentName, isChild, childCount }: MatrixRowProps) {
  const statusMeta = STATUS_META[req.status];
  const ref = formatSourceRef(req.sourceRef);
  const wordUsagePct = req.wordLimit && req.wordCount !== undefined
    ? Math.round((req.wordCount / req.wordLimit) * 100)
    : undefined;
  const isTruncatable = req.text.length > 140;
  const [expanded, setExpanded] = useState(false);

  return (
    <tr className="border-b border-[#e2e8f0] last:border-b-0 align-top">
      <td className="px-4 py-3">
        <div className={`flex items-start gap-1.5 ${isChild ? 'pl-6' : ''}`}>
          {isChild && <CornerDownRight size={12} className="text-slate-300 mt-0.5 flex-shrink-0" />}
          <div className="min-w-0">
            <p className="text-xs font-semibold text-navy">
              {req.clauseRef && <span className="text-slate-400 mr-1.5">{req.clauseRef}</span>}
              {isTruncatable && !expanded ? `${req.text.slice(0, 140)}…` : req.text}
              {isTruncatable && (
                <button
                  type="button"
                  onClick={() => setExpanded(prev => !prev)}
                  className="ml-1.5 text-primary hover:text-primary-dark font-semibold whitespace-nowrap"
                >
                  {expanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </p>
            <p className="text-[11px] text-slate-500 mt-1">
              {documentName && <>{documentName} · </>}
              {req.category && <>{req.category} · </>}
              {ref}
            </p>
            {childCount > 0 && (
              <p className="text-[10px] text-slate-400 mt-1">{childCount} sub-criteria</p>
            )}
            {req.openIssues.length > 0 && (
              <ul className="mt-1 flex flex-wrap gap-1.5">
                {req.openIssues.map((issue, i) => (
                  <li key={i} className="flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                    <AlertTriangle size={10} /> {issue}
                  </li>
                ))}
              </ul>
            )}
            {(req.linkedWinThemeId || req.linkedEvidenceIds?.length) && (
              <p className="text-[10px] text-slate-400 mt-1">
                {req.linkedWinThemeId && <>win theme: {req.linkedWinThemeId} · </>}
                {req.linkedEvidenceIds?.length ? <>evidence: {req.linkedEvidenceIds.join(', ')}</> : null}
              </p>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 border ${statusMeta.className}`}>
          {statusMeta.label}
        </span>
        {req.changeStatus && (
          <span className={`ml-1.5 inline-flex items-center text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 border ${CHANGE_STATUS_META[req.changeStatus]}`}>
            {req.changeStatus}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-[11px] text-slate-500 whitespace-nowrap">
        {req.weight !== undefined && <div>{req.weight}%</div>}
        {(req.potRelativeWeight !== undefined || req.totalBidRelativeWeight !== undefined) && (
          <div className="text-slate-400">
            {req.potRelativeWeight !== undefined && <>{req.potRelativeWeight}% pot</>}
            {req.potRelativeWeight !== undefined && req.totalBidRelativeWeight !== undefined && ' / '}
            {req.totalBidRelativeWeight !== undefined && <>{req.totalBidRelativeWeight}% total</>}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-[11px] text-slate-500 whitespace-nowrap">
        {req.wordLimit !== undefined ? (
          <>
            {req.wordCount ?? 0}/{req.wordLimit}
            {wordUsagePct !== undefined && <span className="text-slate-400"> ({wordUsagePct}%)</span>}
          </>
        ) : '—'}
      </td>
      <td className="px-4 py-3 text-[11px] text-slate-500 whitespace-nowrap">{req.deadline || '—'}</td>
    </tr>
  );
}

interface ComplianceMatrixTableProps {
  requirements: BidRequirement[];
  documentNamesById: Record<string, string>;
}

export function ComplianceMatrixTable({ requirements, documentNamesById }: ComplianceMatrixTableProps) {
  const [statusFilter, setStatusFilter] = useState<BidRequirementStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [documentFilter, setDocumentFilter] = useState<string>('all');
  const [sortKey, setSortKey] = useState<SortKey>('clauseRef');

  const categories = useMemo(
    () => Array.from(new Set(requirements.map(r => r.category).filter((c): c is string => !!c))).sort(),
    [requirements]
  );
  const documentIds = useMemo(
    () => Array.from(new Set(requirements.map(r => r.documentId))),
    [requirements]
  );

  const childrenByParentId = useMemo(() => {
    const map: Record<string, BidRequirement[]> = {};
    for (const r of requirements) {
      if (r.parentRequirementId) {
        (map[r.parentRequirementId] ??= []).push(r);
      }
    }
    return map;
  }, [requirements]);

  const topLevelRows = useMemo(() => {
    let rows = requirements.filter(r => !r.parentRequirementId);
    if (statusFilter !== 'all') rows = rows.filter(r => r.status === statusFilter);
    if (categoryFilter !== 'all') rows = rows.filter(r => r.category === categoryFilter);
    if (documentFilter !== 'all') rows = rows.filter(r => r.documentId === documentFilter);

    const sorted = [...rows].sort((a, b) => {
      if (sortKey === 'clauseRef') return (a.clauseRef || '').localeCompare(b.clauseRef || '');
      if (sortKey === 'status') return a.status.localeCompare(b.status);
      return (b.weight ?? 0) - (a.weight ?? 0);
    });
    return sorted;
  }, [requirements, statusFilter, categoryFilter, documentFilter, sortKey]);

  if (!requirements.length) {
    return <div className="py-12 text-center text-sm text-slate-500">No requirements extracted yet.</div>;
  }

  return (
    <>
      <div className="p-4 border-b border-[#e2e8f0] flex flex-wrap gap-3 items-center">
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as BidRequirementStatus | 'all')}
          className="border border-[#e2e8f0] rounded-xl px-3 py-1.5 text-xs text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
        >
          <option value="all">All statuses</option>
          {(Object.keys(STATUS_META) as BidRequirementStatus[]).map(s => (
            <option key={s} value={s}>{STATUS_META[s].label}</option>
          ))}
        </select>
        {categories.length > 0 && (
          <select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            className="border border-[#e2e8f0] rounded-xl px-3 py-1.5 text-xs text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
          >
            <option value="all">All categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        {documentIds.length > 1 && (
          <select
            value={documentFilter}
            onChange={e => setDocumentFilter(e.target.value)}
            className="border border-[#e2e8f0] rounded-xl px-3 py-1.5 text-xs text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
          >
            <option value="all">All documents</option>
            {documentIds.map(id => <option key={id} value={id}>{documentNamesById[id] || id}</option>)}
          </select>
        )}
        <select
          value={sortKey}
          onChange={e => setSortKey(e.target.value as SortKey)}
          className="border border-[#e2e8f0] rounded-xl px-3 py-1.5 text-xs text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white ml-auto"
        >
          <option value="clauseRef">Sort: clause ref</option>
          <option value="status">Sort: status</option>
          <option value="weight">Sort: weight</option>
        </select>
      </div>
      {!topLevelRows.length ? (
        <div className="py-12 text-center text-sm text-slate-500">No requirements match these filters.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                <th className="px-4 py-2 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Requirement</th>
                <th className="px-4 py-2 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-2 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Weight</th>
                <th className="px-4 py-2 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Words</th>
                <th className="px-4 py-2 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Deadline</th>
              </tr>
            </thead>
            <tbody>
              {topLevelRows.map(req => (
                <React.Fragment key={req.requirementId}>
                  <MatrixRow
                    req={req}
                    documentName={documentNamesById[req.documentId]}
                    isChild={false}
                    childCount={childrenByParentId[req.requirementId]?.length ?? 0}
                  />
                  {(childrenByParentId[req.requirementId] ?? []).map(child => (
                    <MatrixRow
                      key={child.requirementId}
                      req={child}
                      documentName={documentNamesById[child.documentId]}
                      isChild
                      childCount={0}
                    />
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
